// TuitionBoard.js
import React, { useEffect, useMemo, useState, useContext } from "react";

import { useNavigate } from "react-router-dom";
import { GraduationCap, UserRound } from "lucide-react";
import {
  IdCardIcon,
  Users,
  UserPlus,
  Sparkles,
  Filter,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import Tuition_Board from "./TuitionBoard";
import Footer from "../Footer";
import BecomeTutor from "../BecomeTutor";
import TutorComponent from "../TutorComponentBn";
import Testimonials from "../TestimonialBn";

const API_BASE = "https://tn.server.goatteachers.com/api";
const PAGE_SIZE = 10;

// ---- Enhanced Counter Section ----
const TNCounterSection = React.memo(function TNCounterSection() {
  const formatBn = (n) => {
    try {
      return new Intl.NumberFormat("bn-BD").format(n);
    } catch {
      const map = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
      return String(n).replace(/\d/g, (d) => map[d]);
    }
  };

  const targets = React.useMemo(() => {
    const r = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    return {
      liveTutors: r(600, 1000),
      positions: r(600, 1000),
      institutes: 64,
      newTutors: r(54, 100),
    };
  }, []);

  const useCountUp = (to, duration = 1800) => {
    const [val, setVal] = React.useState(0);
    React.useEffect(() => {
      let raf = 0,
        start = 0;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min(1, (ts - start) / duration);
        setVal(Math.floor(p * to));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      return () => cancelAnimationFrame(raf);
    }, [to, duration]);
    return val;
  };

  const v1 = useCountUp(targets.liveTutors);
  const v2 = useCountUp(targets.positions);
  const v3 = useCountUp(targets.institutes);
  const v4 = useCountUp(targets.newTutors);

  const items = [
    {
      icon: IdCardIcon,
      label: "লাইভ টিউশন",
      value: v1,
      gradient: "from-cyan-500 to-blue-500",
    },
    {
      icon: Users,
      label: "লাইভ টিউটর",
      value: v2,
      plus: true,
      gradient: "from-[#25d976] to-emerald-500",
    },
    {
      icon: UserPlus,
      label: "নতুন টিউশন",
      value: v3,
      gradient: "from-violet-500 to-purple-500",
    },
    {
      icon: ShieldCheck,
      label: "ভেরিফাইড টিউটর",
      value: v4,
      gradient: "from-amber-500 to-orange-500",
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      aria-label="site counters"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#25d976]/5 to-transparent" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
      <div
        className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#25d976]/10 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: "1s" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {items.map(({ icon: Icon, label, value, plus, gradient }, i) => (
            <div
              key={i}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/80 to-slate-800/50 backdrop-blur-sm border border-slate-700/50 p-6 hover:border-slate-600 transition-all duration-500 hover:scale-105"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              {/* Glow effect on hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
              />

              {/* Icon with animated ring */}
              <div className="relative mb-4">
                <div
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}
                >
                  <Icon className="text-white" size={24} aria-hidden="true" />
                </div>
                {/* Pulse ring */}
                <div
                  className={`absolute inset-0 w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} animate-ping opacity-20`}
                />
              </div>

              {/* Text content */}
              <div className="relative baloo-da-2-tutor">
                <div className="text-slate-400 text-sm font-medium mb-2">
                  {label}
                </div>
                <div
                  className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}
                >
                  {formatBn(value)}
                  {plus && value >= targets.positions ? "+" : ""}
                </div>
              </div>

              {/* Corner decoration */}
              <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-slate-700/20 rounded-full blur-2xl" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

// ---- Modern How It Works Section ----
function HowItWorksSection() {
  const Card = ({ title, subtitle, steps, icon }) => (
    <div className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900/90 to-slate-800/70 backdrop-blur-xl border border-slate-700/50 hover:border-[#25d976]/50 p-8 transition-all duration-500 hover:shadow-2xl hover:shadow-[#25d976]/20">
      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#25d976]/0 via-[#25d976]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Decorative blurs */}
      <div className="absolute -right-12 -top-12 w-32 h-32 bg-[#25d976]/20 rounded-full blur-3xl group-hover:bg-[#25d976]/30 transition-all duration-500" />
      <div className="absolute -left-12 -bottom-12 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl group-hover:bg-cyan-500/30 transition-all duration-500" />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#25d976] transition-colors duration-300">
              {title}
            </h3>
            <p className="text-slate-400 text-sm font-medium">{subtitle}</p>
          </div>

          {/* Icon badge */}
          <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-[#25d976]/20 to-[#25d976]/5 border border-[#25d976]/30 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
            <span className="text-4xl" aria-hidden="true">
              {icon}
            </span>
          </div>
        </div>

        {/* Steps */}
        <ol className="space-y-6 mb-6">
          {steps.map((s, idx) => (
            <li key={idx} className="flex items-start gap-4 group/step">
              {/* Step number with gradient */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#25d976] to-emerald-500 flex items-center justify-center shadow-lg shadow-[#25d976]/30 group-hover/step:scale-110 group-hover/step:rotate-12 transition-all duration-300">
                <span className="text-slate-900 font-bold text-base">
                  {idx + 1}
                </span>
              </div>

              {/* Step content */}
              <div className="flex-1 pt-1.5">
                <div className="text-white font-bold text-base mb-1 group-hover/step:text-[#25d976] transition-colors duration-200">
                  {s.title}
                </div>
                <div className="text-slate-400 text-sm leading-relaxed">
                  {s.desc}
                </div>
              </div>
            </li>
          ))}
        </ol>

        {/* Footer link */}
        <div className="relative">
          <div className="h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent mb-4" />
          <a
            href="https://goatteachers.com/siteguidelines"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#25d976] hover:text-emerald-400 transition-colors duration-200 group/link"
          >
            <ShieldCheck
              size={16}
              className="group-hover/link:rotate-12 transition-transform duration-300"
            />
            <span className="border-b-2 border-[#25d976]/30 group-hover/link:border-[#25d976] transition-colors duration-200">
              Verification & safety rules
            </span>
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <section className="relative w-full py-20 overflow-hidden">
      {/* Background decorations */}
      {/* <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#25d976]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_transparent_0%,_#0a1929_100%)] pointer-events-none" /> */}

      <div className="relative z-10 mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="text-center mb-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25d976]/10 border border-[#25d976]/30 backdrop-blur-sm mb-6">
            <Sparkles size={16} className="text-[#25d976]" />
            <span className="text-[#25d976] text-sm font-bold">
              সহজ প্রক্রিয়া
            </span>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl md:text-6xl font-black bg-gradient-to-r from-cyan-400 via-[#25d976] to-emerald-400 bg-clip-text text-transparent mb-4 leading-tight baloo-da-2-tutor">
            শুরু করো মাত্র
            <br />
            তিন ধাপে!
          </h1>

          {/* Subheading */}
          <p className="text-slate-400 text-lg max-w-2xl mx-auto baloo-da-2-tutor">
            সহজ এবং দ্রুত প্রক্রিয়ায় তোমার যাত্রা শুরু করো
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto baloo-da-2-tutor">
          <Card
            title="শিক্ষকদের জন্য"
            subtitle="প্রোফাইল → ব্রাউজ → এপলাই।"
            icon={<UserRound size={28} color="white" />}
            steps={[
              {
                title: "প্রোফাইল বানাও",
                desc: "তোমার ব্যাকগ্রাউন্ড ও পছন্দের এলাকা সিলেক্ট করো।",
              },
              {
                title: "টিউশন বোর্ড ব্রাউজ করো",
                desc: "বিষয় ও এরিয়া অনুযায়ী ম্যাচ খুঁজে নাও।",
              },
              {
                title: "এপলাই করো",
                desc: "Apply Now তে ক্লিক করে তোমার প্রস্তাব পাঠাও।",
              },
            ]}
          />

          <Card
            title="শিক্ষার্থীদের জন্য"
            subtitle="টিউশন পোস্ট করো অথবা টিউটর ব্রাউস করো।"
            icon={<GraduationCap size={28} color="white" />}
            steps={[
              {
                title: "Post Tuition এ ক্লিক করে টিউশন পোস্ট করো",
                desc: "প্রোফাইল দেখো এবং দ্রুত শর্টলিস্ট করো।",
              },
              {
                title: "টিউটর ব্রাউস করো",
                desc: "তোমার ক্লাস, বিষয় ও লোকেশন শেয়ার করো।",
              },
              {
                title: "ডাইরেক্ট কানেক্ট করো",
                desc: "সেরা শিক্ষক বেছে নিয়ে শেখা শুরু করো।",
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
}

export default function TuitionBoard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  return (
    <>
      <TNCounterSection />
      <TutorComponent />
      <HowItWorksSection />
      <Testimonials />

      {/* <div>
        <h2 className="text-center text-4xl font-black leading-tight text-white sm:text-4xl lg:text-5xl baloo-da-2-tutor">
          <span className="relative inline-block align-baseline">
            <span className="bg-gradient-to-r from-[#25d976] to-emerald-400 bg-clip-text text-transparent">
              আজই
            </span>

            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 -bottom-2 h-1 w-full rounded-full bg-gradient-to-r from-[#25d976] to-emerald-400"
            />
          </span>

          <span className="text-white"> — টিউটর হও</span>
        </h2>
      </div>

      <BecomeTutor /> */}
      <Footer />
    </>
  );
}
